import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { Button, Tooltip } from '@material-ui/core';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import Typography from '@material-ui/core/Typography';
import { green } from '../../Constants/Colors';
import clsx from 'clsx';
import IconDownload from './icons/IconDownload';
import IconUpload from './icons/IconUpload';
import IconMail from './icons/IconMail';
import IconPDF from './icons/IconPDF';

const useStyles = makeStyles(
  (theme) => ({
    ToolbarContainer: {},
    VerticalToolbarContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    btn: {
      textTransform: 'none',
      transition: 'color 0.3s ease-in-out',
      '&:hover': {
        background: 'initial',
        color: green,
      },
    },
    iconBtn: {
      width: '36px',
      height: '25px',
      padding: '2px',
      minWidth: '0px',
    },
    btnSave: {
      width: '26px',
      height: '19px',
    },
    btnShare: {
      width: '24px',
      height: '18px',
    },
    btnPrint: {
      width: '18px',
      height: '24px',
    },
    btnLoad: {
      width: '26px',
      height: '19px',
    },
    buttonLabel: {
      fontFamily: 'Inter',
      fontWeight: 400,
      paddingLeft: '5px',
      paddingRight: '15px',
      color: 'inherit',
    },
    highlight: {
      color: green,
    },
  }),
  {
    name: 'Toolbar',
  }
);

interface Props {
  saveEnabled?: boolean;
  shareEnabled?: boolean;
  printEnabled?: boolean;
  loadEnabled?: boolean;
  showLabels?: boolean;
  highlightSave?: boolean;
  vertical?: boolean;
  onMenuButtonClicked?: Function;
}

export const Toolbar: React.FC<Props> = ({
  saveEnabled = true,
  shareEnabled = true,
  printEnabled = true,
  loadEnabled = false,
  showLabels = true,
  highlightSave = false,
  vertical = false,
  onMenuButtonClicked = () => null,
  ...props
}) => {
  const classes = useStyles();

  const btnSave = (
    <Button
      className={showLabels ? classes.btn : classes.iconBtn}
      onClick={() => {
        onMenuButtonClicked();
        Services.interaction.saveConfiguration();
      }}
    >
      <IconDownload className={classes.btnSave} />
      {showLabels && (
        <Typography variant="body1" className={clsx(classes.buttonLabel, highlightSave && classes.highlight)}>
          {t('toolbar.saveConfiguration')}
        </Typography>
      )}
    </Button>
  );

  const btnLoad = (
    <Button
      className={showLabels ? classes.btn : classes.iconBtn}
      onClick={() => {
        onMenuButtonClicked();
        Services.interaction.loadConfiguration();
      }}
    >
      <IconUpload className={classes.btnSave} />
      {showLabels && (
        <Typography variant="body1" className={classes.buttonLabel}>
          {t('toolbar.loadConfiguration')}
        </Typography>
      )}
    </Button>
  );

  const btnShare = (
    <Button
      className={showLabels ? classes.btn : classes.iconBtn}
      onClick={async () => {
        onMenuButtonClicked();
        await Services.interaction.shareConfiguration();
        Services.ui.show('sendEmail');
      }}
    >
      <IconMail className={classes.btnSave} />
      {showLabels && (
        <Typography variant="body1" className={classes.buttonLabel}>
          {t('toolbar.shareConfiguration')}
        </Typography>
      )}
    </Button>
  );

  const btnPrint = (
    <Button
      className={showLabels ? classes.btn : classes.iconBtn}
      onClick={() => {
        onMenuButtonClicked();
        Services.interaction.printConfigurationAndOpenPdf();
      }}
    >
      <IconPDF className={classes.btnSave} />
      {showLabels && (
        <Typography variant="body1" className={classes.buttonLabel}>
          {t('toolbar.printConfigurationPdf')}
        </Typography>
      )}
    </Button>
  );

  return (
    <div className={vertical ? classes.VerticalToolbarContainer : classes.ToolbarContainer}>
      <>
        {saveEnabled &&
          (showLabels ? (
            btnSave
          ) : (
            <Tooltip title={t('toolbar.saveConfiguration')} arrow>
              {btnSave}
            </Tooltip>
          ))}
        {loadEnabled &&
          (showLabels ? (
            btnLoad
          ) : (
            <Tooltip title={t('toolbar.loadConfiguration')} arrow>
              {btnLoad}
            </Tooltip>
          ))}
        {shareEnabled &&
          (showLabels ? (
            btnShare
          ) : (
            <Tooltip title={t('toolbar.shareConfiguration')} arrow>
              {btnShare}
            </Tooltip>
          ))}
        {printEnabled &&
          (showLabels ? (
            btnPrint
          ) : (
            <Tooltip title={t('toolbar.printConfigurationPdf')} arrow>
              {btnPrint}
            </Tooltip>
          ))}
      </>
    </div>
  );
};
